import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Trash2, Plus, GripVertical } from 'lucide-react';
import { InputSwitch } from 'primereact/inputswitch';
import { ListColumn, CanvasElement } from './types';
import { CustomNumberInput } from './CustomNumberInput';

interface ListColumnsModalProps {
  visible: boolean;
  onHide: () => void;
  element: CanvasElement;
  onSave: (columns: ListColumn[], itemSource: string, showHeader: boolean) => void;
}

export const ListColumnsModal: React.FC<ListColumnsModalProps> = ({
  visible,
  onHide,
  element,
  onSave
}) => {
  const [columns, setColumns] = useState<ListColumn[]>([]);
  const [itemSource, setItemSource] = useState<string>('items');
  const [showHeader, setShowHeader] = useState<boolean>(true);

  useEffect(() => {
    if (visible) {
      setColumns(element.listConfig?.columns || []);
      setItemSource(element.listConfig?.itemSource || 'items');
      setShowHeader(element.listConfig?.showHeader ?? true);
    }
  }, [visible, element]);

  const handleAddColumn = () => {
    setColumns([
      ...columns,
      {
        id: Math.random().toString(36).substr(2, 9),
        header: 'Yeni Sütun',
        width: 30,
        content: '',
        align: 'left'
      }
    ]);
  };

  const handleUpdateColumn = (index: number, updates: Partial<ListColumn>) => {
    const newCols = [...columns];
    newCols[index] = { ...newCols[index], ...updates };
    setColumns(newCols);
  };

  const handleRemoveColumn = (index: number) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave(columns, itemSource, showHeader);
    onHide();
  };

  const alignOptions = [
    { label: 'Sol', value: 'left' },
    { label: 'Orta', value: 'center' },
    { label: 'Sağ', value: 'right' }
  ];

  return (
    <Dialog
      header="Liste Sütunları (Columns)"
      visible={visible}
      style={{ width: '800px' }}
      onHide={onHide}
      footer={
        <div className="flex justify-end gap-2">
          <Button label="İptal" icon="pi pi-times" onClick={onHide} className="p-button-text" />
          <Button label="Kaydet" icon="pi pi-check" onClick={handleSave} autoFocus />
        </div>
      }
    >
      <div className="bg-primary/10 border border-primary/20 rounded p-3 mb-4 text-xs text-text/80">
        <strong className="text-primary block mb-1">Bilgi:</strong>
        Bu liste yazdırılırken her bir satır için <code>item</code> değişkeni oluşur. Sütun içeriğine <code>{`{{item.sku}}`}</code>, <code>{`{{item.quantity}} Adet`}</code> gibi formatta değişkenler yazabilirsiniz.
      </div>

      <div className="flex flex-col gap-4 mb-6 bg-bgAlt p-3 rounded border border-borderColor">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-xs font-bold text-text/70 block mb-1">Veri Kaynağı (Dizi)</label>
            <InputText
              value={itemSource}
              onChange={(e) => setItemSource(e.target.value)}
              className="w-full p-inputtext-sm"
              placeholder="Örn: items veya physicalItems"
            />
            <span className="text-[10px] text-text/50 mt-1 block">Önizleme ve çıktı alırken hangi verinin listeleneceğini belirler.</span>
          </div>
          <div className="w-48 flex flex-col items-end justify-center">
            <label className="text-xs font-bold text-text/70 block mb-2">Başlıkları Göster</label>
            <InputSwitch
              checked={showHeader}
              onChange={(e) => setShowHeader(e.value || false)}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {/* Başlıklar */}
        {columns.length > 0 && (
          <div className="flex items-center gap-2 px-2 text-xs font-bold text-text/70 mb-1">
            <div className="w-6"></div>
            <div className="flex-1">Başlık (Header)</div>
            <div className="flex-1">Genişlik (mm)</div>
            <div className="flex-[2]">İçerik Şablonu</div>
            <div className="w-24">Hizalama</div>
            <div className="w-8"></div>
          </div>
        )}

        {columns.map((col, index) => (
          <div key={col.id} className="flex items-center gap-2 bg-bgAlt p-2 rounded border border-borderColor">
            <div className="w-6 flex justify-center cursor-move text-text/40 hover:text-text/70">
              <GripVertical className="w-4 h-4" />
            </div>

            <div className="flex-1">
              <InputText
                value={col.header}
                onChange={(e) => handleUpdateColumn(index, { header: e.target.value })}
                className="w-full p-inputtext-sm"
                placeholder="Örn: SKU"
              />
            </div>

            <div className="flex-1 flex items-center gap-1">
              <CustomNumberInput
                value={col.width}
                onChange={(val) => handleUpdateColumn(index, { width: val })}
                min={1}
              />
              <span className="text-xs text-text/50">mm</span>
            </div>

            <div className="flex-[2]">
              <InputText
                value={col.content}
                onChange={(e) => handleUpdateColumn(index, { content: e.target.value })}
                className="w-full p-inputtext-sm"
                placeholder="Örn: {{item.sku}}"
              />
            </div>

            <div className="w-24">
              <Dropdown
                value={col.align}
                options={alignOptions}
                onChange={(e) => handleUpdateColumn(index, { align: e.value })}
                className="w-full p-dropdown-sm"
              />
            </div>

            <div className="w-8 flex justify-center">
              <Button
                icon={<Trash2 className="w-4 h-4" />}
                onClick={() => handleRemoveColumn(index)}
                className="p-button-danger p-button-text p-2 hover:bg-danger/10 text-danger"
                title="Sütunu Sil"
              />
            </div>
          </div>
        ))}

        <Button
          label="Yeni Sütun Ekle"
          icon={<Plus className="w-4 h-4 mr-2" />}
          onClick={handleAddColumn}
          className="p-button-outlined p-button-sm w-48 mt-2"
        />
      </div>
    </Dialog>
  );
};
